import { useAuth0 } from '@auth0/auth0-react';
import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { getMyPermissionConfig, getConfig, IAuthConfig } from '../AuthApi';
import * as Navigation from '../../../modules/Navigation';
import { useNavigate } from 'react-router';
import { HospitalUserClaim, CellTrakOperatorClaim } from './ClaimTypes';
import { FormMemory } from '../../../components/app/entityComponents/Form/FormMemory';


export interface AccessPermissions{
    [key: string]: Permissions[];
}
// Define the shape of the permissions object
interface Permissions {

    accessType: string
    accessConcern: string;
}

// Define the context value type
export interface PermissionContextType {
    permissions: AccessPermissions | undefined;
    userScope: string | undefined;
    hospitalUserClaim: HospitalUserClaim | null | undefined;
    cellTrakOperatorClaim: CellTrakOperatorClaim | null | undefined;
}

// Create the context with a default value
const PermissionContext = createContext<PermissionContextType | undefined>(undefined);

// Create a provider component
interface PermissionProviderProps {
    children: ReactNode;
}

export const PermissionProvider: React.FC<PermissionProviderProps> = ({ children }) => {
    const [permissions, setPermissions] = useState<AccessPermissions | undefined>();
    const [userScope, setUserScope] = useState<string | undefined>();
    const [hospitalUserClaim, setHospitalUserClaim] = useState<HospitalUserClaim | null | undefined>();
    const [cellTrakOperatorClaim, setCellTrakOperatorClaim] = useState<CellTrakOperatorClaim | null | undefined>();
    const [authConfig, setAuthConfig] = useState<IAuthConfig | null>(null);
    const { user, isAuthenticated, getAccessTokenSilently, loginWithRedirect } = useAuth0();
    const navigate = useNavigate();
    
    // Load auth config on mount
    useEffect(() => {
        async function loadAuthConfig() {
            const config = await getConfig();
            setAuthConfig(config);
        }
        loadAuthConfig();
    }, []);
    
    // Ensure offline_access is included in scope for refresh tokens
    function getScope(): string {
        if (!authConfig) return 'openid offline_access';
        const scope = authConfig.scope || '';
        const scopes = scope.split(' ').filter(s => s.trim());
        if (!scopes.includes('offline_access')) {
            scopes.push('offline_access');
        }
        return scopes.join(' ');
    }
    
    async function updateAccessToken() {
        if (!authConfig) {
            console.warn('Auth config not loaded yet, skipping token update');
            return;
        }
        
        try {
            const accessToken = await getAccessTokenSilently({
                authorizationParams: {
                    audience: authConfig.audience,
                    scope: getScope()
                }
            });
            localStorage.setItem((process.env.REACT_APP_ACCESS_TOKEN as string), accessToken)
        } catch (e: any) {
            console.warn('Failed to get access token silently; redirecting to login.', e);
            await loginWithRedirect();
        }
    }

    async function callBack() {
        await updateAccessToken();

        const result = await getMyPermissionConfig();
        setPermissions(result.permissions);
        setUserScope(result.userScope);
        setHospitalUserClaim(result.hospitalUserClaim || null);
        setCellTrakOperatorClaim(result.cellTrakOperatorClaim || null);
      }

    useEffect(() => {
        if (isAuthenticated && user && authConfig) {
            // Clear FormMemory on login to prevent form values from previous sessions
            FormMemory.clearAll();
            callBack();
        }
    }, [user, getAccessTokenSilently, isAuthenticated, authConfig]);

    return (
        <PermissionContext.Provider value={{ permissions, userScope, hospitalUserClaim, cellTrakOperatorClaim }}>
            {permissions && children}
        </PermissionContext.Provider>
    );
};

// Custom hook to use the PermissionContext
export const usePermissions = (): PermissionContextType => {
    const context = useContext(PermissionContext);
    if (context === undefined) {
        throw new Error('usePermissions must be used within a PermissionProvider');
    }
    return context;
};

// Custom hook to get UserScope
export const useUserScope = (): string | undefined => {
    const context = useContext(PermissionContext);
    if (context === undefined) {
        throw new Error('useUserScope must be used within a PermissionProvider');
    }
    return context.userScope;
};
